import { firstValueFrom, timeout } from 'rxjs';

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { API } from '@models/API/index';

import { SessionTokenStore } from '@cxc/core/services';
import { platformAPI } from '@services/API/endpoints';

const REFRESH_LOCK = 'cxc-session-refresh';

/** It runs in an APP_INITIALIZER: a hung identity-api must not hold the boot loader. */
export const REFRESH_TIMEOUT_MS = 15_000;

/**
 * Gets a new Cognito access token from identity-api (LEN-2764).
 *
 * The refresh token never reaches JavaScript: it travels in an HttpOnly
 * cookie, so the request only needs `withCredentials`. The new access token
 * is kept in memory by `SessionTokenStore`.
 */
@Injectable({ providedIn: 'root' })
export class SessionRefreshService {
  private http = inject(HttpClient);
  private tokens = inject(SessionTokenStore);

  private inFlight: Promise<string> | null = null;
  private callers: (AbortSignal | undefined)[] = [];
  /**
   * Bumped by `discardInFlightToken()`. A request keeps the value it started
   * with, so one that predates a cleared session never writes to the store
   * (LEN-2945).
   */
  private storeEpoch = 0;

  /**
   * One refresh at a time in this tab (every caller shares it) and, where the
   * browser has Web Locks, one at a time across tabs.
   *
   * A promise cannot be cancelled, so a caller that gives up passes `signal`:
   * once every caller of the shared refresh aborted, its token is dropped
   * instead of stored, and the request is skipped if it had not started.
   */
  refresh(signal?: AbortSignal): Promise<string> {
    if (!this.inFlight) {
      const callers: (AbortSignal | undefined)[] = [signal];
      const epoch = this.storeEpoch;
      this.callers = callers;
      this.inFlight = this.withCrossTabLock(() =>
        this.request(callers, epoch)
      ).finally(() => (this.inFlight = null));
    } else {
      this.callers.push(signal);
    }
    return this.inFlight;
  }

  /**
   * Keeps whatever is in flight from writing its token to the store, without
   * failing the callers that are still waiting for it.
   *
   * Aborting is not enough: the token is only dropped once *every* caller
   * aborted, and the interceptor joins the shared refresh with no signal at
   * all (`http-authorization.interceptor.ts`), so `every()` never holds. A
   * `restore()` that gave up would clear the session and then watch that
   * refresh put an access token back, leaving a token in memory with the user
   * logged out (LEN-2945).
   *
   * The callers still get the token: the interceptor retries with the value
   * this promise resolves to, not with what the store holds, so its request
   * survives.
   *
   * A caller that reads the store instead of the resolved value loses that
   * write. `operations.component.ts` is one — it drops the resolved token and
   * reloads the grid trusting that the store carries the new facility's claim,
   * so a discard landing inside its refresh would reload with the old token and
   * no error. Today the only discard is `SessionService.restore()`, in an
   * APP_INITIALIZER, which cannot overlap it; a second caller would have to
   * take the resolved value (MR !1318 review S1).
   */
  discardInFlightToken(): void {
    this.storeEpoch++;
  }

  /**
   * A failure leaves the token in memory on purpose: if it ends in a logout,
   * SessionService still sends the revoke with it, and logOut() clears it.
   */
  private async request(
    callers: (AbortSignal | undefined)[],
    epoch: number
  ): Promise<string> {
    // The Web Lock wait runs outside REFRESH_TIMEOUT_MS and can outlast the
    // callers: no one left to use the token, no reason to rotate the cookie.
    this.throwIfAbandoned(callers);
    const response = await firstValueFrom(
      this.http
        .post<API.Identity.Auth.RefreshToken.Response>(
          platformAPI.identity.auth.refreshToken,
          {},
          {
            withCredentials: true,
            headers: new HttpHeaders({ 'Loader-Opt-In': 'false' }),
          }
        )
        .pipe(timeout(REFRESH_TIMEOUT_MS))
    );
    // A token arriving after its callers gave up (SessionService.restore()
    // already cleared the session) must not be stored.
    this.throwIfAbandoned(callers);
    if (epoch === this.storeEpoch) {
      this.tokens.set(response.access_token);
    }
    return response.access_token;
  }

  private throwIfAbandoned(callers: (AbortSignal | undefined)[]): void {
    if (callers.every(signal => signal?.aborted)) {
      throw new DOMException('Every caller aborted the refresh', 'AbortError');
    }
  }

  private withCrossTabLock<T>(task: () => Promise<T>): Promise<T> {
    const locks = (navigator as Navigator & { locks?: LockManager }).locks;
    return locks ? locks.request(REFRESH_LOCK, task) : task();
  }
}
